import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import Utils, {createUUId, deepEqual, numComparator} from "../../../../../../../core/services/utils/utils";
import {Autocomplete, Box, Button, Card, Checkbox, createFilterOptions, Fade, FormControlLabel, IconButton, MenuItem, Paper, Skeleton, TextField, Typography} from "@mui/material";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import axios from "axios";
import {NexusSearchSchemaEntryNames, NexusSearchSchemaTypes, ProductTechnicalPropertyTypeIds} from "../../../../../../../core/constants/enums";
import Collapse from "@mui/material/Collapse";
import classnames from "classnames";
import {ArrowDownwardOutlined, ArrowDropDownCircleOutlined, North, South, UnfoldLessRounded, UnfoldMoreRounded} from "@mui/icons-material";
import LookupSearchVinDecoder from "../vin-decoder";
import useAccessLevels from "../../../../../../hooks/use-access-levels";


const LookupSearchViewSidebarSearch = ({
                                           facets,
                                           searchSchema,
                                           filters,
                                           setFilters,
                                           orderBy,
                                           setOrderBy,
                                           featureFlags,
                                           readonly,
                                           currentIndustry,
                                           initialVin,
                                       }) => {
    const [selectedFilters, setSelectedFilters] = useState(filters ?? []);
    const [expandingAll, setExpandingAll] = useState(false);
    const [collapsingAll, setCollapsingAll] = useState(false);
    const operatorAccessLevels = useAccessLevels();
    const canShowVinDecoder = useMemo(() => (
        operatorAccessLevels.packages.lookup.vinDecoder
        && Utils.isVinDecoderIndustry(currentIndustry)
    ), [currentIndustry, operatorAccessLevels.packages.lookup.vinDecoder]);

    const filtersChanged = useMemo(() => {
        const filtersTidied = filters.reduce((acc, e) => {
            acc[e.filterId] = [...(acc[e.filterId] ?? []), e.value]
            return acc;
        }, {})
        const selectedFiltersTidied = selectedFilters.reduce((acc, e) => {
            acc[e.filterId] = [...(acc[e.filterId] ?? []), e.value]
            return acc;
        }, {})
        return !deepEqual(filtersTidied, selectedFiltersTidied);
    }, [selectedFilters, filters])

    const groupedSchemas = useMemo(() => {
        const flatGroups = {};
        for (const facet of facets) {
            const schemaEntry = searchSchema.entries.find(s => s.name === facet.name);
            if (!schemaEntry)
                continue;
            const parentSchema = searchSchema.entries.find(s => s.id === schemaEntry.parentId);
            let values = [];
            if (featureFlags.hidePropertyGroupFacetMostFrequentValues && schemaEntry.type === NexusSearchSchemaTypes.propertyGroup) {
                values.push(...facet.values.filter(e => selectedFilters.some(f => f.filterId === schemaEntry.id && (
                    f.value === e.value ||
                    Utils.formatTechnicalPropertyValueDisplay(schemaEntry.dataType, f.value, schemaEntry.nullReplacement) === Utils.formatTechnicalPropertyValueDisplay(schemaEntry.dataType, e.value, schemaEntry.nullReplacement)
                ))) ?? []);
            } else {
                values.push(...facet.values);
            }
            values = values.map(e => {
                return ({
                    value: e.value,
                    count: e.affectedPartsCount,
                    display: `${Utils.formatTechnicalPropertyValueDisplay(schemaEntry.dataType, e.value?.toString(), schemaEntry.nullReplacement)}`,
                })
            })
            flatGroups[schemaEntry.id] = {
                key: schemaEntry.id,
                title: schemaEntry.title,
                parentId: schemaEntry.parentId,
                facets: [
                    ...flatGroups[schemaEntry.id]?.facets ?? [],
                    {
                        schema: {
                            ...schemaEntry,
                            originalParentId: parentSchema?.originalId === 0 ? null : parentSchema?.originalId,
                        },
                        values: values,
                    }
                ],
            };
        }
        const groupArray = Object.values(flatGroups);
        const values = Object.values(
            groupArray.reduce((acc, e) => {
                const parentSchema = searchSchema.entries.find(s => s.id === e.parentId);
                const schema = e.facets[0].schema;
                const type = schema.type;
                let typeTitle = '';
                switch (type) {
                    case NexusSearchSchemaTypes.field:
                        typeTitle = 'Field';
                        break;
                    case NexusSearchSchemaTypes.property:
                        typeTitle = 'Technical';
                        break;
                    case NexusSearchSchemaTypes.propertyGroup:
                        typeTitle = 'Property Group';
                        break;
                    default:
                        break;
                }
                const groupTitle = [NexusSearchSchemaEntryNames.parentCategory, NexusSearchSchemaEntryNames.category].includes(schema.name)
                    ? "Category"
                    : type === NexusSearchSchemaTypes.field
                        ? ""
                        : typeTitle;
                const key = parentSchema?.id ?? groupTitle;
                acc[key] = {
                    key: key,
                    title: parentSchema?.title ?? groupTitle,
                    schema: parentSchema,
                    facets: [...(acc[key]?.facets ?? []), ...e.facets]
                }
                return acc;
            }, {})
        )

        const getOrder = (title) => {
            switch (title) {
                case 'Category':
                    return 1;
                case 'Technical':
                    return 2;
                case '':
                    return 3;
                default:
                    return 0; // Treat property group titles as the highest priority (comes first)
            }
        };
        values.sort((a, b) => {
            const orderA = getOrder(a.title);
            const orderB = getOrder(b.title);

            // Compare based on custom order first
            if (orderA !== orderB) {
                return orderA - orderB;
            }

            // If the order is the same, fallback to comparing the schema orderIndex
            return numComparator(a.schema.orderIndex, b.schema.orderIndex);
        }).forEach(group => {
            group.facets.sort((a, b) => numComparator(a.schema.orderIndex, b.schema.orderIndex,))
        })
        return values;
    }, [facets, featureFlags.hidePropertyGroupFacetMostFrequentValues, searchSchema.entries, selectedFilters])

    /**
     * Order by options
     */
    const orderByOptions = useMemo(() => {
        const sortableEntries = searchSchema.entries.filter(e => e.sortable);
        return sortableEntries.reduce((acc, entry) => {
            acc.push(
                {
                    isFacet: entry.isFacet,
                    entry: entry,
                    value: `desc__${entry.name}`,
                    filtersValue: {id: entry.id, isDescending: true,},
                    isDescending: true,
                    display: entry.title,
                },
                {
                    isFacet: entry.isFacet,
                    entry: entry,
                    value: `asc__${entry.name}`,
                    filtersValue: {id: entry.id, isDescending: false,},
                    isDescending: false,
                    display: entry.title,
                })
            return acc;
        }, []);
    }, [searchSchema.entries])

    const dropdownOrderByOptions = useMemo(() => orderByOptions, [orderByOptions])
    const facetsOrderByOptions = useMemo(() => orderByOptions.filter(e => e.isFacet), [orderByOptions])

    const newestFirstOption = useMemo(() => {
        const createdDateTimeEntry = searchSchema.entries.find(e => e.name === NexusSearchSchemaEntryNames.submitDateTime);
        if (!createdDateTimeEntry)
            return null;
        return {
            isFacet: false,
            entry: createdDateTimeEntry,
            value: `desc__${createdDateTimeEntry.name}`,
            filtersValue: {id: createdDateTimeEntry.id, isDescending: true,},
            isDescending: true,
            display: 'Newest first',
        }
    }, [searchSchema.entries])

    const newestFirstChecked = useMemo(() => {
        if (!newestFirstOption)
            return false;
        return orderBy?.id === newestFirstOption.entry.id && orderBy?.isDescending === newestFirstOption.isDescending;
    }, [orderBy, newestFirstOption])

    /**
     * Toggles the newest first filter.
     * @type {(function(): void)|*}
     */
    const toggleNewestFirst = useCallback(() => {
        if (!newestFirstOption)
            return;
        if (newestFirstChecked) {
            setOrderBy(null);
        } else {
            setOrderBy(newestFirstOption.filtersValue);
        }
    }, [newestFirstChecked, newestFirstOption, setOrderBy])

    /**
     * Sets the selected facets
     */
    useEffect(() => {
        setSelectedFilters(filters);
    }, [filters])

    /**
     * Applies the selected filters onto the parents' filters.
     * @type {(function(): void)}
     */
    const applyFilters = useCallback(() => {
        setFilters(selectedFilters.map(e => ({filterId: e.filterId, value: e.value})));
    }, [setFilters, selectedFilters])

    /**
     * Expands All the rows of the data-grid.
     * @type {(function())}
     */
    const expandAll = useCallback(async () => {
        setExpandingAll(true);
        const buttons = document.querySelectorAll('.facet-toggler-button');
        for (const button of buttons) {
            if (!button.classList.contains('open')) {
                button.click();
            }
        }
        setExpandingAll(false);
    }, [])

    /**
     * Collapses All the rows of the data-grid.
     * @type {(function())}
     */
    const collapseAll = useCallback(async () => {
        setCollapsingAll(true);
        const buttons = document.querySelectorAll('.facet-toggler-button');
        for (const button of buttons) {
            if (button.classList.contains('open')) {
                button.click();
            }
        }
        setCollapsingAll(false);
    }, [])

    // /**
    //  * Applies the vin decoder filters, replacing any previously selected filters.
    //  * @type {(function(*): void)|*}
    //  */
    // const applyVinDecoderFilters = useCallback((filters) => {
    //     const propertyGroupEntryIds = searchSchema.entries.filter(e => e.type === NexusSearchSchemaTypes.propertyGroup).map(e => e.id);
    //     const alreadySelectedFilters = selectedFilters
    //         .map(e => ({filterId: e.filterId, value: e.value}))
    //         .filter(e => !propertyGroupEntryIds.includes(e.filterId))
    //     ;
    //     setFilters([
    //         ...alreadySelectedFilters,
    //         ...filters.map(e => ({filterId: e.filterId, value: e.value})),
    //     ]);
    // }, [searchSchema.entries, selectedFilters, setFilters]);

    /**
     * Applies the vin decoder filters, replacing any previously selected filters.
     * @type {(function(*): void)|*}
     */
    const applyVinDecoderFilters = useCallback((filters) => {
        setFilters(filters.map(e => ({filterId: e.filterId, value: e.value})));
    }, [setFilters]);

    return (
        <>
            <Box
                sx={{
                    boxShadow: 'none',
                    marginBlock: 2,
                    marginInline: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    position: 'relative'
                }}
            >
                {
                    canShowVinDecoder &&
                    <LookupSearchVinDecoder
                        // Key by industry only — including initialVin remounted the field empty
                        // when resetQueryKeyword cleared query.vin after the first search.
                        key={currentIndustry?.id ?? 'vin-decoder'}
                        industryId={currentIndustry?.id}
                        initialVin={initialVin}
                        readonly={readonly}
                        searchSchema={searchSchema}
                        applyFilters={applyVinDecoderFilters}
                    />
                }
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                    {
                        !!orderByOptions.length &&
                        <>
                            <Typography variant={'caption'} fontWeight={600} color={'text.secondary'}>
                                Order by
                            </Typography>
                            <TextField
                                select
                                variant={'outlined'}
                                size={'small'}
                                disabled={readonly}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: () => {
                                        const found = orderByOptions.find(s => deepEqual(s.filtersValue, orderBy));
                                        if (!found)
                                            return 'Sort your results here'
                                        return `${found.display} ${found.isDescending ? '(descending)' : '(ascending)'}`
                                    }
                                }}
                                onChange={(e) => setOrderBy(orderByOptions.find(s => s.value === e.target.value)?.filtersValue)}
                            >
                                <MenuItem
                                    value={null}
                                    className={'d-flex align-items-center'}
                                    sx={{
                                        '.fill': {color: 'primary.main'}
                                    }}
                                >
                                    <Typography variant={'body2'}>
                                        None
                                    </Typography>
                                </MenuItem>
                                {
                                    dropdownOrderByOptions.map(e => (
                                        <MenuItem
                                            key={e.value}
                                            value={e.value}
                                            className={'d-flex align-items-center'}
                                            sx={{
                                                '.fill': {
                                                    color: 'primary.main',
                                                }
                                            }}
                                        >
                                            {e.isDescending ? <South/> : <North/>}
                                            <Typography variant={'body2'}>
                                                {e.display ?? ""}
                                            </Typography>
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </>
                    }
                    {
                        newestFirstOption &&
                        <Button
                            variant={newestFirstChecked ? 'contained' : 'outlined'}
                            size={'small'}
                            color={'tertiary'}
                            className={'d-flex align-items-center'}
                            disabled={readonly}
                            onClick={toggleNewestFirst}
                        >
                            <ArrowDownwardOutlined className={'icon small mr-1'}/>
                            Newest Products First
                        </Button>
                    }
                    <Box sx={{display: 'flex', gap: 1, justifyContent: 'space-evenly'}}>
                        <Button
                            variant={'outlined'}
                            size={'small'}
                            color={'secondary'}
                            className={'d-flex align-items-center flex-grow-1'}
                            disabled={expandingAll || collapsingAll}
                            onClick={() => expandAll()}
                        >
                            <UnfoldMoreRounded className={'icon small mr-1'}/>
                            Expand all
                        </Button>
                        <Button
                            variant={'outlined'}
                            color={'secondary'}
                            size={'small'}
                            className={'d-flex align-items-center  flex-grow-1'}
                            disabled={expandingAll || collapsingAll}
                            onClick={() => collapseAll()}
                        >
                            <UnfoldLessRounded className={'icon small mr-1'}/>
                            Collapse all
                        </Button>
                    </Box>
                </Box>
                {
                    groupedSchemas.map(group => (
                        <LookupSearchViewSidebarSearchGroup
                            readonly={readonly}
                            key={group.key}
                            group={group}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            orderBy={orderBy}
                            setOrderBy={setOrderBy}
                            orderByOptions={facetsOrderByOptions}
                            hidePropertyGroupFacetMostFrequentValues={featureFlags.hidePropertyGroupFacetMostFrequentValues}
                            sidebarFacetsCollapsedByDefault={featureFlags.sidebarFacetsCollapsedByDefault}
                        />
                    ))
                }
                {
                    filtersChanged &&
                    <Box sx={{position: 'sticky', bottom: 4, left: 8, right: 8,}}>
                        <Fade in>
                            <Button variant={'contained'} disabled={readonly} onClick={applyFilters} sx={{width: '100%'}}>
                                Apply Changes (search)
                            </Button>
                        </Fade>
                    </Box>
                }
            </Box>
        </>
    );
}

export default LookupSearchViewSidebarSearch;


const LookupSearchViewSidebarSearchGroup = ({
                                                readonly,
                                                group,
                                                selectedFilters,
                                                setSelectedFilters,
                                                orderBy,
                                                setOrderBy,
                                                orderByOptions,
                                                hidePropertyGroupFacetMostFrequentValues,
                                                sidebarFacetsCollapsedByDefault,
                                            }) => {


    const propertyGroupAppliedFilters = useMemo(() => {
        if (group.key === 'Technical')
            return null;
        const selectedFacetValues = selectedFilters.filter(e => group.facets.some(f => f.schema.id === e.filterId));
        const object = {};
        for (const selectedFacetValue of selectedFacetValues) {
            // if (selectedFacetValue.temp) {
            //     continue;
            // }
            const schema = group.facets.find(f => f.schema.id === selectedFacetValue.filterId)?.schema;
            object[schema.name] = {
                name: schema.title,
                orderIndex: schema.orderIndex,
                values: [...(object[schema.name]?.values ?? []), selectedFacetValue.value?.toString() ?? (schema.nullReplacement ? schema.nullReplacement.toString() : null)],
                nullReplacement: schema.nullReplacement,
            }
        }
        return Object.values(object);
    }, [group?.facets, group?.key, selectedFilters])

    return (
        <>
            <Card
                key={group.key}
                variant={'outlined'}
                sx={{
                    padding: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Paper sx={{boxShadow: 'none', padding: 1, display: 'flex', flexDirection: 'column', gap: 1}}>
                    <Typography variant={'body2'} fontWeight={600} color={'secondary.main'}>
                        {group.title ?? ""}
                    </Typography>
                    {
                        group.facets.map(facet => (
                            <MemoLookupSearchViewSidebarSearchFacet
                                readonly={readonly}
                                key={facet.schema?.name}
                                facet={facet}
                                selectedFilters={selectedFilters}
                                setSelectedFilters={setSelectedFilters}
                                propertyGroupAppliedFilters={propertyGroupAppliedFilters}
                                orderBy={orderBy}
                                setOrderBy={setOrderBy}
                                orderByOptions={orderByOptions}
                                hidePropertyGroupFacetMostFrequentValues={hidePropertyGroupFacetMostFrequentValues}
                                sidebarFacetsCollapsedByDefault={sidebarFacetsCollapsedByDefault}
                            />
                        ))
                    }
                </Paper>
            </Card>
        </>
    );
}

const LookupSearchViewSidebarSearchFacet = ({
                                                readonly,
                                                facet,
                                                selectedFilters,
                                                setSelectedFilters,
                                                propertyGroupAppliedFilters,
                                                orderBy,
                                                setOrderBy,
                                                orderByOptions,
                                                hidePropertyGroupFacetMostFrequentValues,
                                                sidebarFacetsCollapsedByDefault,
                                            }) => {
    const mustHideCollapseState = useMemo(() => facet.schema?.type === NexusSearchSchemaTypes.propertyGroup && hidePropertyGroupFacetMostFrequentValues, [facet.schema?.type, hidePropertyGroupFacetMostFrequentValues])
    const [collapsed, setCollapsed] = useState(sidebarFacetsCollapsedByDefault ? !mustHideCollapseState : false);
    const hasOrderBy = useMemo(() => orderByOptions.some(e => e.entry.id === facet.schema?.id), [orderByOptions, facet.schema])
    const orderByApplied = useMemo(() => {
        if (!hasOrderBy || !orderBy?.id)
            return false
        return orderBy.id === facet.schema?.id;
    }, [facet.schema?.id, hasOrderBy, orderBy?.id])
    const orderByDescending = useMemo(() => {
        if (!orderBy)
            return false;
        return orderBy.isDescending;
    }, [orderBy])

    /**
     * Changes the order by value
     * @type {(function(): void)}
     */
    const changeOrderBy = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!hasOrderBy)
            return;
        const values = orderByOptions.filter(e => e.entry.id === facet.schema?.id);
        if (!orderByApplied) {
            setOrderBy(values.find(e => e.isDescending === false)?.filtersValue);
        } else if (!orderByDescending) {
            setOrderBy(values.find(e => e.isDescending === true)?.filtersValue);
        } else {
            setOrderBy(null);
        }
    }, [facet.schema?.id, hasOrderBy, orderByApplied, orderByOptions, setOrderBy, orderByDescending])

    /**
     * Changes the facet value
     * @type {(function(*, *): void)}
     */
    const changeFacetValue = useCallback((checked, value) => {
        setSelectedFilters(prevState => {
            if (checked) {
                return [...prevState, {filterId: facet.schema?.id, value: value, temp: true}]
            }
            return prevState.filter(e => !(e.filterId === facet.schema?.id && (
                e.value === value
                || Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, e.value, facet.schema?.nullReplacement) === Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, value, facet.schema?.nullReplacement)
            )))
        })
    }, [facet.schema?.dataType, facet.schema?.id, facet.schema?.nullReplacement, setSelectedFilters])

    /**
     * Adds a filter form the autocomplete raw value.
     * @type {(function(*): void)}
     */
    const addFilterFromAutocomplete = useCallback((rawStringValue) => {
        const value = Utils.parseTechnicalPropertyValue(facet.schema?.dataType, rawStringValue);
        setSelectedFilters(prevState => {
            const found = prevState.find(e => e.filterId === facet.schema?.id && (
                e.value === value
                || Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, e.value, facet.schema?.nullReplacement) === Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, value, facet.schema?.nullReplacement)
            ));
            if (found)
                return prevState;
            return [...prevState, {filterId: facet.schema?.id, value: rawStringValue, temp: true}]
        })
    }, [facet.schema?.dataType, facet.schema?.id, facet.schema?.nullReplacement, setSelectedFilters])

    const facetValues = useMemo(() => {
        const values = [...facet.values];
        for (const selectedFilter of selectedFilters) {
            if (selectedFilter.filterId !== facet.schema?.id)
                continue;
            if (!values.some(e => {
                return e.value === selectedFilter.value
                    || Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, selectedFilter.value, facet.schema?.nullReplacement) === Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, e.value, facet.schema?.nullReplacement)
            })) {
                values.push({
                    value: selectedFilter.value,
                    count: '--',
                    display: Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, selectedFilter.value, facet.schema?.nullReplacement)
                })
            }
        }
        return values.map(value => ({
            ...value,
            selected: selectedFilters.some(e => e.filterId === facet.schema?.id && (
                e.value === value.value
                || Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, e.value, facet.schema?.nullReplacement) === Utils.formatTechnicalPropertyValueDisplay(facet.schema?.dataType, value.value, facet.schema?.nullReplacement)
            )),
        }));
    }, [facet.schema?.dataType, facet.schema?.id, facet.schema?.nullReplacement, facet.values, selectedFilters])


    const autocompleteFilters = useMemo(() => {
        if (!propertyGroupAppliedFilters)
            return null;
        const filters = {};
        for (const propertyGroupAppliedFilter of propertyGroupAppliedFilters) {
            if (propertyGroupAppliedFilter.values.length > 0 && propertyGroupAppliedFilter.name !== facet.schema?.title) {
                // we check for nullReplacement too, so if any of the values is null or nullReplacement:
                if (propertyGroupAppliedFilter.values.includes(null) || propertyGroupAppliedFilter.values.includes(propertyGroupAppliedFilter.nullReplacement)) {
                    filters[propertyGroupAppliedFilter.name] = null;
                } else {
                    filters[propertyGroupAppliedFilter.name] = propertyGroupAppliedFilter.values;
                }
            }
        }
        return filters;
    }, [facet.schema?.title, propertyGroupAppliedFilters])

    /**
     * Toggles the collapsed state.
     * @type {(function(): void)|*}
     */
    const ToggleCollapsedState = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        setCollapsed(prevState => !prevState);
    }, [])

    const title = useMemo(() => {
        const title = facet.schema?.title;
        if (title.endsWith('y')) {
            return title.slice(0, title.length - 1) + 'ies';
        }
        if (title.endsWith('s')) {
            return title + 'es';
        }
        return title + 's';
    }, [facet.schema?.title]);

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{
                    padding: 1,
                    borderColor: 'divider',
                    display: 'flex', flexDirection: 'column', gap: collapsed ? 0 : 1,
                    ...(!mustHideCollapseState && {
                        cursor: 'pointer',
                    }),
                }}
                {...(!mustHideCollapseState && {
                    onClick: ToggleCollapsedState,
                })}
            >
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between'}}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {facet.schema?.title ?? ""}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}>
                        {
                            hasOrderBy &&
                            <IconButton
                                size={'small'}
                                color={'primary'}
                                onClick={changeOrderBy}
                                disabled={readonly}
                                className={classnames('p-0')}
                            >
                                <South
                                    className={'icon small'}
                                    sx={(theme) => ({
                                        transition: theme.transitions.create(['transform', 'opacity'], {
                                            duration: theme.transitions.duration.shortest,
                                        }),
                                        opacity: orderByApplied ? 1 : 0.5,
                                        ...(orderByApplied ? {
                                            transform: `rotate(${orderByDescending ? '0deg' : '180deg'})`,
                                        } : {})
                                    })}
                                />
                            </IconButton>
                        }
                        {
                            !mustHideCollapseState &&
                            <IconButton
                                size={'small'}
                                color={'primary'}
                                onClick={ToggleCollapsedState}
                                className={classnames('p-0 facet-toggler-button', {'open': !collapsed})}
                            >
                                <ArrowDropDownCircleOutlined
                                    className={'icon small'}
                                    sx={(theme) => ({
                                        transition: theme.transitions.create('transform', {
                                            duration: theme.transitions.duration.shortest,
                                        }),
                                        transform: `rotate(${collapsed ? '0deg' : '180deg'})`,
                                    })}
                                />
                            </IconButton>
                        }
                    </Box>
                </Box>
                <Collapse in={!collapsed} orientation={'vertical'}>
                    <Box
                        sx={{display: 'flex', flexDirection: 'column', gap: 1, cursor: 'default'}}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {
                            (
                                facet.schema?.name === NexusSearchSchemaEntryNames.tag ||
                                facet.schema?.type === NexusSearchSchemaTypes.property ||
                                facet.schema?.type === NexusSearchSchemaTypes.propertyGroup ||
                                facet.schema?.name === NexusSearchSchemaEntryNames.parentCategory
                            ) &&
                            <div/>
                        }
                        {
                            facet.schema?.name === NexusSearchSchemaEntryNames.tag &&
                            <LookupSearchViewSidebarSearchTagAutocomplete
                                disabled={readonly}
                                industryId={facet.schema?.industryId}
                                onSelect={addFilterFromAutocomplete}
                            />
                        }
                        {
                            facet.schema?.type === NexusSearchSchemaTypes.property &&
                            <LookupSearchViewSidebarSearchPropertyAutocomplete
                                disabled={readonly}
                                title={title}
                                entryId={facet.schema?.originalId}
                                typeId={facet.schema?.dataType}
                                hasPredefinedValues={facet.schema?.hasPreDefinedValues}
                                nullReplacement={facet.schema?.nullReplacement}
                                onSelect={addFilterFromAutocomplete}
                            />
                        }
                        {
                            facet.schema?.type === NexusSearchSchemaTypes.propertyGroup &&
                            <LookupSearchViewSidebarSearchPropertyGroupAutocomplete
                                disabled={readonly}
                                title={title}
                                entryId={facet.schema?.originalId}
                                parentId={facet.schema?.originalParentId}
                                onSelect={addFilterFromAutocomplete}
                                nullReplacement={facet.schema?.nullReplacement}
                                filters={autocompleteFilters}
                            />
                        }
                        {
                            facet.schema?.name === NexusSearchSchemaEntryNames.parentCategory &&
                            <LookupSearchViewSidebarSearchCategoryAutocomplete
                                disabled={readonly}
                                title={title}
                                industryId={facet.schema?.industryId}
                                onSelect={addFilterFromAutocomplete}
                            />
                        }
                        {
                            (
                                facet.schema?.name === NexusSearchSchemaEntryNames.tag ||
                                facet.schema?.type === NexusSearchSchemaTypes.property ||
                                facet.schema?.type === NexusSearchSchemaTypes.propertyGroup ||
                                facet.schema?.name === NexusSearchSchemaEntryNames.parentCategory
                            ) && (!hidePropertyGroupFacetMostFrequentValues || facet.schema?.type !== NexusSearchSchemaTypes.propertyGroup) &&
                            <Typography variant={'caption'} color={'text.secondary'} sx={{marginBlockStart: 1}}>
                                Most Frequent Values
                            </Typography>
                        }
                        {
                            facetValues.map((data) => (
                                <MemoLookupSearchViewSidebarSearchFacetCheckbox
                                    disabled={readonly}
                                    key={`${data.value}-${data.count}-${data.display}`}
                                    data={data}
                                    typeId={facet.schema?.dataType}
                                    changeFacetValue={changeFacetValue}
                                />
                            ))
                        }
                    </Box>
                </Collapse>
            </Card>
        </>
    );
}

const MemoLookupSearchViewSidebarSearchFacet = memo(LookupSearchViewSidebarSearchFacet);

const LookupSearchViewSidebarSearchFacetCheckbox = ({data, changeFacetValue, disabled, typeId}) => {
    return (
        <>
            <FormControlLabel
                disabled={disabled}
                slotProps={{
                    typography: {
                        sx: {width: '100%'}
                    },
                }}
                label={
                    <Box sx={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant={'caption'} fontWeight={600}>
                            {data.display ?? ""}
                            <Typography variant={'caption'} color={'text.secondary'} sx={{marginInlineStart: 1}}>
                                {
                                    typeId === ProductTechnicalPropertyTypeIds.color &&
                                    (data.value ?? "")
                                }
                            </Typography>
                        </Typography>
                        <Typography variant={'caption'} color={'tertiary.main'} fontWeight={600}>
                            {data.count ?? 0}
                        </Typography>
                    </Box>
                }
                sx={{margin: 0}}
                control={
                    <Checkbox
                        sx={{padding: 0.5}}
                        disableRipple
                        disabled={disabled}
                        checked={data.selected}
                        onChange={(e, checked) => changeFacetValue(checked, data.value)}
                    />
                }
            />
        </>
    );
}

const MemoLookupSearchViewSidebarSearchFacetCheckbox = memo(LookupSearchViewSidebarSearchFacetCheckbox);

const LookupSearchViewSidebarSearchTagAutocomplete = ({industryId, onSelect, disabled}) => {
    const [options, setOptions] = useState([]);
    const [getOptions, setGetOptions] = useState(false);
    const [keywords, setKeywords] = useState('');
    const [value, setValue] = useState('');
    const isMounted = useIsMounted();
    /**
     * Fetches the suggested options from the api.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchSuggestedOptions = useCallback(async (keywords, cancelToken) => {
        if (!isMounted())
            return;
        // TODO: call api to get all system tags.
        if (!keywords) {
            setOptions([]);
            return;
        }
        const response = await Api.getTagsAutocomplete(keywords, industryId, cancelToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const options = response.data?.map(e => ({title: e.name, id: e.id})) ?? [];
            options.sort((a, b) => a.title.localeCompare(b.title));
            setOptions(options);
        }
    }, [industryId, isMounted])

    /**
     * With each change in the keywords:
     * - fetches the suggested options.
     */
    useEffect(() => {
        if (!getOptions)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        setTimeout(() => void fetchSuggestedOptions(keywords, cancelTokenSource.token), 1000);
        return () => cancelTokenSource.cancel();
    }, [fetchSuggestedOptions, keywords, getOptions])


    /**
     * Selects the provided value only if its exists.
     * @type {(function(*, *): void)}
     */
    const onChange = useCallback((e, value) => {
        if (!value)
            return;
        onSelect(value.title);
        setValue(prevState => prevState === '' ? ' ' : '');
    }, [onSelect])

    return (
        <>
            <Autocomplete
                fullWidth
                readOnly={disabled}
                disabled={disabled}
                onFocus={() => setGetOptions(true)}
                options={options}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={onChange}
                size={'small'}
                getOptionLabel={(option) => option?.title ?? ""}
                getOptionKey={(option) => option?.id ?? ""}
                value={value}
                onInputChange={(_, v) => setKeywords(v)}
                componentsProps={{
                    clearIndicator: {color: 'default'},
                    popupIndicator: {color: 'default'},
                }}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        size={'small'}
                        label={'search'}
                        InputProps={{
                            ...params.InputProps,
                        }}
                    />
                }
            />
        </>
    );
}


const LookupSearchViewSidebarSearchCategoryAutocomplete = ({title, industryId, onSelect, disabled}) => {
    const [options, setOptions] = useState([]);
    const [getOptions, setGetOptions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mockValue, setMockValue] = useState('');
    const isMounted = useIsMounted();

    /**
     * Fetches the suggested options from the api.
     * @type {(function(): Promise<void>)}
     */
    const fetchSuggestedOptions = useCallback(async () => {
        if (!isMounted())
            return;
        setLoading(true);
        const response = await Api.listPartCategoryNew(industryId);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            const options = response.data?.filter(e => !e.parentId)?.map(e => ({title: e.title, id: e.id})) ?? [];
            options.sort((a, b) => a.title.localeCompare(b.title));
            setOptions(options);
        }
    }, [industryId, isMounted])

    /**
     * With each change in the keywords:
     * - fetches the suggested options.
     */
    useEffect(() => {
        if (!getOptions)
            return;
        void fetchSuggestedOptions();
    }, [fetchSuggestedOptions, getOptions])


    /**
     * Selects the provided value only if its exists.
     * @type {(function(*, *): void)}
     */
    const onChange = useCallback((e, value) => {
        if (!value)
            return;
        onSelect(value.title);
        setMockValue(prevState => prevState === '' ? ' ' : '');
    }, [onSelect])

    const filterOptions = useMemo(() => createFilterOptions({
        ignoreCase: true,
        limit: 100,
        stringify: (option) => option.title ?? ""
    }), []);

    return (
        <>
            <Autocomplete
                readOnly={disabled}
                disabled={disabled}
                fullWidth
                onFocus={() => setGetOptions(true)}
                options={options}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={onChange}
                size={'small'}
                getOptionLabel={(option) => option?.title ?? ""}
                getOptionKey={(option) => option?.id ?? ""}
                filterOptions={filterOptions}
                value={mockValue}
                loading={loading}
                loadingText={
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexDirection: 'column'}}>
                        {
                            Array.from({length: 5}).map((_, e) => (
                                <Skeleton key={`index-${e}`} width={'100%'} height={50} sx={{transform: 'none'}}/>
                            ))
                        }
                    </Box>
                }
                componentsProps={{
                    clearIndicator: {color: 'default'},
                    popupIndicator: {color: 'default'},
                }}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        size={'small'}
                        label={`All ${title}`}
                        InputProps={{
                            ...params.InputProps,
                        }}
                    />
                }
            />
        </>
    );
}


const LookupSearchViewSidebarSearchPropertyGroupAutocomplete = ({
                                                                    title,
                                                                    entryId,
                                                                    parentId,
                                                                    onSelect,
                                                                    filters,
                                                                    nullReplacement,
                                                                    disabled,
                                                                }) => {
    const [options, setOptions] = useState([]);
    const [getOptions, setGetOptions] = useState(false);
    const isMounted = useIsMounted();
    const [mockValue, setMockValue] = useState('');
    const [loading, setLoading] = useState(false);
    const fetchedFilters = useRef();

    /**
     * Fetches the suggested options from the api.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchSuggestedOptions = useCallback(async (cancelToken) => {
        const parsedFilters = Object.fromEntries(Object.entries(filters).map(([key, values]) => {
            if (Array.isArray(values)) {
                const sortedValues = [...values];
                sortedValues.sort((a, b) => a.localeCompare(b));
                return [key, sortedValues]
            }
            return [key, values];
        }));
        if (fetchedFilters.current && deepEqual(fetchedFilters.current, parsedFilters))
            return;
        fetchedFilters.current = parsedFilters;
        const forApi = {
            industryGroupPropertyId: parentId,
            searchQuery: parsedFilters,
            returnAttrId: entryId,
        }
        setLoading(true);
        const response = await Api.queryIndustryGroupProperties(forApi, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            const options = response.data?.map(e => ({
                title: e.value?.toString() ?? nullReplacement,
                value: e.value
            })) ?? [];
            options.sort((a, b) => a.title.localeCompare(b.title));
            setOptions(options);
        }
    }, [filters, parentId, entryId, isMounted, nullReplacement])

    /**
     * With each change in the keywords:
     * - fetches the suggested options.
     */
    useEffect(() => {
        if (!getOptions)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchSuggestedOptions(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [fetchSuggestedOptions, getOptions])

    /**
     * Selects the provided value only if its exists.
     * @type {(function(*): void)}
     */
    const onChange = useCallback((e, value) => {
        if (!value)
            return;
        onSelect(value?.value);
        setMockValue(prevState => prevState === '' ? ' ' : '');
    }, [onSelect])

    const filterOptions = useMemo(() => createFilterOptions({
        ignoreCase: true,
        limit: 100,
        stringify: (option) => option?.title ?? ""
    }), []);


    return (
        <>
            <Autocomplete
                fullWidth
                readOnly={disabled}
                disabled={disabled}
                onFocus={() => setGetOptions(true)}
                options={options}
                onChange={onChange}
                size={'small'}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                getOptionLabel={(option) => option?.title ?? ""}
                getOptionKey={(option) => option?.value ?? ""}
                filterOptions={filterOptions}
                value={mockValue}
                loading={loading}
                loadingText={
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexDirection: 'column'}}>
                        {
                            Array.from({length: 5}).map((_, e) => (
                                <Skeleton key={`index-${e}`} width={'100%'} height={50} sx={{transform: 'none'}}/>
                            ))
                        }
                    </Box>
                }
                componentsProps={{
                    clearIndicator: {color: 'default'},
                    popupIndicator: {color: 'default'},
                }}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        size={'small'}
                        label={`All ${title}`}
                        InputProps={{
                            ...params.InputProps,
                        }}
                    />
                }
            />
        </>
    );
}

const LookupSearchViewSidebarSearchPropertyAutocomplete = ({
                                                               title,
                                                               entryId,
                                                               hasPredefinedValues,
                                                               onSelect,
                                                               typeId,
                                                               nullReplacement,
                                                               disabled,
                                                           }) => {
    const [options, setOptions] = useState([]);
    const [getOptions, setGetOptions] = useState(false);
    const [keywords, setKeywords] = useState('');
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState('');

    const isMounted = useIsMounted();

    /**
     * Fetches the predefined options from the api.
     * @type {(function(*): Promise<void>)}
     */
    const fetchPredefinedOptions = useCallback(async (cancelToken) => {
        setLoading(true);
        const response = await Api.listIndustryPropertyPropertyValues(entryId, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            const options = response.data?.map(e => ({
                title: Utils.formatTechnicalPropertyValueDisplay(typeId, e.content, nullReplacement),
                value: e.content
            })) ?? [];
            options.sort((a, b) => a.title.localeCompare(b.title));
            setOptions(options);
        }
    }, [isMounted, entryId, typeId, nullReplacement])

    /**
     * Fetches the suggested options from the api.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchSuggestedOptions = useCallback(async (keywords, cancelToken) => {
        if (!isMounted())
            return;
        if (!keywords) {
            setOptions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        if (typeId === ProductTechnicalPropertyTypeIds.fraction) {
            const fraction = Utils.technicalPropertyFractionToDecimal(keywords);
            if (fraction) {
                keywords = fraction.toString();
            }
        }
        if (typeId === ProductTechnicalPropertyTypeIds.boolean) {
            let trimmed = keywords.toLowerCase()?.trim() ?? '';
            if (trimmed === 'true') {
                keywords = 'true';
            } else if (trimmed === 'false') {
                keywords = 'false';
            } else if (trimmed === 'yes') {
                keywords = 'true';
            } else if (trimmed === 'no') {
                keywords = 'false';
            } else if (trimmed === '1') {
                keywords = 'true';
            } else if (trimmed === '0') {
                keywords = 'false';
            }
        }
        const forApi = {
            industryPropertyId: entryId,
            keyword: keywords
        }
        // TODO: call api to get all of the property values.
        const response = await Api.autoSuggestIndustryPropertyValue(forApi, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            const options = [...new Set(response.data?.map(e => e.value) ?? [])]
                .map(e => ({
                    title: Utils.formatTechnicalPropertyValueDisplay(typeId, e, nullReplacement),
                    value: e,
                    id: createUUId(true),
                }));
            options.sort((a, b) => a.title.localeCompare(b.title));
            setOptions(options);
        }
    }, [isMounted, entryId, typeId, nullReplacement])

    /**
     * With each change in the keywords:
     * - fetches the suggested options.
     */
    useEffect(() => {
        if (!getOptions || !hasPredefinedValues)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchPredefinedOptions(cancelTokenSource.token)
        return () => cancelTokenSource.cancel();
    }, [fetchPredefinedOptions, getOptions, hasPredefinedValues])

    /**
     * With each change in the keywords:
     * - fetches the suggested options.
     */
    useEffect(() => {
        if (!getOptions || hasPredefinedValues)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        setTimeout(() => void fetchSuggestedOptions(keywords, cancelTokenSource.token), 1000);
        return () => cancelTokenSource.cancel();
    }, [fetchSuggestedOptions, keywords, getOptions, hasPredefinedValues])


    /**
     * Selects the provided value only if its exists.
     * @type {(function(*, *): void)}
     */
    const onChange = useCallback((e, value) => {
        if (!value)
            return;
        onSelect(value.value);
        setValue(prevState => prevState === '' ? ' ' : '');
    }, [onSelect])

    const filterOptions = useMemo(() => createFilterOptions({
        ignoreCase: true,
        limit: 100,
        stringify: (option) => typeId === ProductTechnicalPropertyTypeIds.color ? option?.value ?? "" : option?.title ?? ""
    }), [typeId]);

    return (
        <>
            <Autocomplete
                fullWidth
                readOnly={disabled}
                disabled={disabled}
                onFocus={() => setGetOptions(true)}
                options={options}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={onChange}
                size={'small'}
                getOptionLabel={(option) => {
                    if (typeId === ProductTechnicalPropertyTypeIds.color) {
                        return option?.value ?? ""
                    }
                    return option?.title ?? "";
                }}
                {...(typeId === ProductTechnicalPropertyTypeIds.color && {
                    renderOption: (props, option) => {
                        const {key, ...rest} = props;
                        return (
                            <Box {...rest} key={key} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                {Utils.formatTechnicalPropertyValueDisplay(ProductTechnicalPropertyTypeIds.color, option?.value, '')}
                                <Typography variant={'caption'}>
                                    {option?.value ?? ""}
                                </Typography>
                            </Box>
                        );
                    }
                })}
                getOptionKey={(option) => option?.id ?? ""}
                value={value}
                loading={loading}
                {
                    ...(hasPredefinedValues ? {
                        filterOptions: filterOptions,
                    } : {})
                }
                {
                    ...(typeId === ProductTechnicalPropertyTypeIds.boolean ? {
                        filterOptions: (values) => values,
                    } : {})
                }
                loadingText={
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexDirection: 'column'}}>
                        {
                            Array.from({length: 5}).map((_, e) => (
                                <Skeleton key={`index-${e}`} width={'100%'} height={50} sx={{transform: 'none'}}/>
                            ))
                        }
                    </Box>
                }
                onInputChange={(_, v) => setKeywords(v)}
                componentsProps={{
                    clearIndicator: {color: 'default'},
                    popupIndicator: {color: 'default'},
                }}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        size={'small'}
                        label={`All ${title}`}
                        InputProps={{
                            ...params.InputProps,
                        }}
                    />
                }
            />
        </>
    );
}
